import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DownloadSimple, Trash, ShieldCheck, Warning } from "@phosphor-icons/react";

export default function DataRightsPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  async function exportData() {
    setDownloading(true);
    try {
      const { data } = await api.get("/users/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `caminhos-meus-dados-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Arquivo baixado com seus dados.");
    } catch (e) { toast.error("Erro ao exportar."); }
    finally { setDownloading(false); }
  }

  async function deleteAccount() {
    if (confirm !== "EXCLUIR") { toast.error("Digite EXCLUIR para confirmar."); return; }
    setDeleting(true);
    try {
      await api.delete("/users/me");
      await logout();
      toast.success("Conta excluída. Todos os seus dados foram apagados.");
      nav("/login");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setDeleting(false); }
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-4xl space-y-6 sm:space-y-8">
      <header className="fade-up">
        <div className="pill-badge bg-white text-[#1E5FFF] border border-[#1E5FFF]/20 mb-4">
          <ShieldCheck size={12} weight="fill" /> LGPD • Direitos do titular
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-[#12143A] leading-tight">Meus Dados</h1>
        <p className="text-zinc-500 mt-2 text-sm sm:text-base max-w-2xl">
          Você pode baixar todos os seus dados armazenados na plataforma ou solicitar a exclusão definitiva da sua conta,
          conforme o Art. 18 da LGPD.
        </p>
      </header>

      <div className="card-soft p-6 fade-up">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#E0EBFF] flex items-center justify-center flex-shrink-0">
            <DownloadSimple size={22} weight="duotone" color="#1E5FFF" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-black">Exportar meus dados</h2>
            <p className="text-sm text-zinc-600 mt-1">
              Baixe um arquivo JSON contendo seu perfil, agenda, notas, pagamentos, planos de estudo,
              histórico do Tutor IA e sessões de tutoria. Portabilidade garantida em formato aberto.
            </p>
            <Button data-testid="export-data-btn" onClick={exportData} disabled={downloading}
                    className="mt-4 btn-pill h-11 gradient-primary hover:opacity-90 text-white">
              <DownloadSimple size={16} className="mr-2" weight="bold" /> {downloading ? "Preparando..." : "Baixar meus dados (JSON)"}
            </Button>
          </div>
        </div>
      </div>

      {user?.role !== "admin" && (
        <div className="card-soft p-6 fade-up border-l-4 border-[#FF3D71]">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#FFE9EE] flex items-center justify-center flex-shrink-0">
              <Warning size={22} weight="duotone" color="#FF3D71" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-heading text-xl font-black text-[#FF3D71]">Excluir minha conta</h2>
              <p className="text-sm text-zinc-600 mt-1">
                Esta ação é <b>irreversível</b>. Todos os seus dados serão apagados imediatamente do banco:
                perfil, agenda, notas, pagamentos, planos, chats e sessões.
              </p>
              <div className="mt-4">
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Digite EXCLUIR para confirmar</label>
                <input data-testid="delete-confirm" value={confirm} onChange={e => setConfirm(e.target.value)}
                       placeholder="EXCLUIR"
                       className="mt-2 w-full h-12 px-4 rounded-2xl border border-zinc-200 focus:border-[#FF3D71] focus:ring-2 focus:ring-[#FF3D71]/20 outline-none" />
                <Button data-testid="delete-account-btn" onClick={deleteAccount} disabled={deleting || confirm !== "EXCLUIR"}
                        variant="destructive"
                        className="mt-3 btn-pill h-11 bg-[#FF3D71] hover:bg-[#e63363] text-white disabled:opacity-40 disabled:cursor-not-allowed">
                  <Trash size={16} className="mr-2" weight="bold" /> {deleting ? "Excluindo..." : "Excluir minha conta"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {user?.role === "admin" && (
        <div className="card-soft p-5 bg-zinc-50 text-sm text-zinc-600">
          Contas administradoras não podem ser excluídas por este painel. Entre em contato com o suporte:
          <a href="mailto:midiatecnointerativa@gmail.com" className="text-[#1E5FFF] font-semibold underline ml-1">midiatecnointerativa@gmail.com</a>.
        </div>
      )}

      <div className="text-xs text-zinc-500">
        Para outros direitos (correção, revogação de consentimento, reclamação à ANPD), consulte nossa{" "}
        <a href="/privacy" className="text-[#1E5FFF] underline">Política de Privacidade</a>.
      </div>
    </div>
  );
}
